import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Building2, Plus, RefreshCw, Shield, ShieldOff, Eye, Trash2,
  AlertCircle, CheckCircle, Users, ClipboardCheck, FileText,
  ChevronDown, ChevronUp, X
} from 'lucide-react';

export default function FranchiseManagerPage() {
  const { api, user } = useAuth();
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [expandedId, setExpandedId] = useState(null);
  const [franchiseStats, setFranchiseStats] = useState({});
  const [viewingFranchise, setViewingFranchise] = useState(null);

  const loadFranchises = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/franchises');
      setFranchises(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load franchises');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadFranchises(); }, [loadFranchises]);

  // Check if currently viewing a franchise
  useEffect(() => {
    if (user?.viewing_franchise) {
      setViewingFranchise(user.viewing_franchise);
    }
  }, [user]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);
    try {
      await api.post('/franchises', form);
      setSuccess(`Franchise "${form.name}" created successfully! Login: ${form.email}`);
      setForm({ name: '', email: '', password: '' });
      setShowCreate(false);
      loadFranchises();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create franchise');
    } finally {
      setCreating(false);
    }
  };

  const handleSuspend = async (id, name) => {
    if (!window.confirm(`Suspend "${name}"? All their users will be blocked from logging in.`)) return;
    try {
      await api.put(`/franchises/${id}/suspend`);
      loadFranchises();
      setSuccess(`"${name}" has been suspended.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to suspend');
    }
  };

  const handleActivate = async (id, name) => {
    try {
      await api.put(`/franchises/${id}/activate`);
      loadFranchises();
      setSuccess(`"${name}" has been reactivated.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to activate');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`PERMANENTLY DELETE "${name}" and ALL their data? This cannot be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type the franchise name to confirm.`)) return;
    try {
      await api.delete(`/franchises/${id}`);
      loadFranchises();
      setSuccess(`"${name}" has been permanently deleted.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleViewFranchise = async (id) => {
    try {
      const { data } = await api.post(`/franchises/${id}/view`);
      setViewingFranchise(data.name);
      setSuccess(`Now viewing "${data.name}" in read-only mode. Navigate to any page to see their data.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enter franchise view');
    }
  };

  const handleExitView = async () => {
    try {
      await api.post('/franchises/exit-view');
      setViewingFranchise(null);
      setSuccess('Exited franchise view. You are now back to your own data.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to exit view');
    }
  };

  const loadStats = async (franchiseId) => {
    if (franchiseStats[franchiseId]) return;
    try {
      const { data } = await api.get(`/franchises/${franchiseId}/stats`);
      setFranchiseStats(prev => ({ ...prev, [franchiseId]: data }));
    } catch { /* ignore */ }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      loadStats(id);
    }
  };

  return (
    <div data-testid="franchise-manager-page" className="space-y-6">
      {/* Viewing Banner */}
      {viewingFranchise && (
        <div data-testid="franchise-view-banner" className="flex items-center justify-between p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
          <div className="flex items-center gap-3">
            <Eye size={20} className="text-amber-600" />
            <div>
              <p className="text-sm font-bold text-amber-800">READ-ONLY MODE — Viewing Franchise Data</p>
              <p className="text-xs text-amber-600">You are viewing "{viewingFranchise}" data. All pages show their data. You cannot make changes.</p>
            </div>
          </div>
          <button data-testid="exit-franchise-view-btn" onClick={handleExitView} className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors">
            <X size={14} /> Exit View
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Franchise Manager</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage franchise CRM copies. Each franchise gets a clean, independent CRM instance.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="refresh-franchises-btn" onClick={loadFranchises} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
          <button data-testid="create-franchise-btn" onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] transition-colors">
            <Plus size={14} /> New Franchise
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div data-testid="franchise-error" className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      {success && (
        <div data-testid="franchise-success" className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle size={16} /> {success}
          <button onClick={() => setSuccess('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <div data-testid="create-franchise-form" className="bg-white border-2 border-[#D35400] rounded-xl p-6">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <Building2 size={18} className="text-[#D35400]" /> Create New Franchise
          </h2>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Franchise Business Name</label>
              <input
                data-testid="franchise-name-input"
                type="text" required value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. RTR Auckland"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Admin Email</label>
              <input
                data-testid="franchise-email-input"
                type="email" required value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="franchise@example.com"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Admin Password</label>
              <input
                data-testid="franchise-password-input"
                type="password" required value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Min 6 characters"
                minLength={6}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]"
              />
            </div>
            <div className="md:col-span-3 flex items-center gap-3">
              <button data-testid="submit-franchise-btn" type="submit" disabled={creating} className="flex items-center gap-2 px-5 py-2.5 bg-[#D35400] text-white rounded-lg text-sm font-medium hover:bg-[#A04000] disabled:opacity-50 transition-colors">
                {creating ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
                {creating ? 'Creating...' : 'Create Franchise'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
            </div>
          </form>
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-xs text-slate-600"><strong>What gets created:</strong> A clean CRM instance with shared config (dropdowns, product catalog, suppliers, GST rates, estimate rates, roof conditions). No client data, invoices, or job data from your CRM is included. The franchise admin will set up their own business info on first login.</p>
          </div>
        </div>
      )}

      {/* Franchise List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-slate-400 text-sm">Loading franchises...</div>
        ) : franchises.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Building2 size={40} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-sm font-semibold text-slate-700 mb-1">No Franchises Yet</h3>
            <p className="text-xs text-slate-500">Click "New Franchise" to create the first franchise CRM instance.</p>
          </div>
        ) : (
          franchises.map(f => {
            const isExpanded = expandedId === f._id;
            const stats = franchiseStats[f._id];
            const isSuspended = f.status === 'suspended';
            return (
              <div key={f._id} data-testid={`franchise-card-${f._id}`} className={`bg-white border rounded-xl overflow-hidden transition-colors ${isSuspended ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
                <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => toggleExpand(f._id)}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSuspended ? 'bg-red-100' : 'bg-[#D35400]/10'}`}>
                    <Building2 size={20} className={isSuspended ? 'text-red-500' : 'text-[#D35400]'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-[#0F172A]">{f.name}</h3>
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${isSuspended ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                        {f.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{f.admin_email} &middot; Created {new Date(f.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <div className="text-center"><div className="font-bold text-slate-700">{f.client_count}</div><div>Clients</div></div>
                    <div className="text-center"><div className="font-bold text-slate-700">{f.appraisal_count}</div><div>Jobs</div></div>
                    <div className="text-center"><div className="font-bold text-slate-700">{f.invoice_count}</div><div>Invoices</div></div>
                  </div>
                  {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-100 px-5 py-4 space-y-4">
                    {/* Stats */}
                    {stats && (
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {Object.entries(stats).map(([col, count]) => (
                          <div key={col} className="p-2 bg-slate-50 rounded-lg text-center">
                            <div className="text-sm font-bold text-slate-700">{count.toLocaleString()}</div>
                            <div className="text-[10px] text-slate-500">{col.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <button
                        data-testid={`view-franchise-${f._id}`}
                        onClick={(e) => { e.stopPropagation(); handleViewFranchise(f._id); }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                      >
                        <Eye size={13} /> View Data (Read-Only)
                      </button>

                      {isSuspended ? (
                        <button
                          data-testid={`activate-franchise-${f._id}`}
                          onClick={(e) => { e.stopPropagation(); handleActivate(f._id, f.name); }}
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors"
                        >
                          <Shield size={13} /> Reactivate
                        </button>
                      ) : (
                        <button
                          data-testid={`suspend-franchise-${f._id}`}
                          onClick={(e) => { e.stopPropagation(); handleSuspend(f._id, f.name); }}
                          className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors"
                        >
                          <ShieldOff size={13} /> Suspend
                        </button>
                      )}

                      <button
                        data-testid={`delete-franchise-${f._id}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(f._id, f.name); }}
                        className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors ml-auto"
                      >
                        <Trash2 size={13} /> Delete Permanently
                      </button>
                    </div>

                    <div className="text-[10px] text-slate-400">
                      Database: {f.db_name} &middot; Users: {f.user_count} &middot; Created by: {f.created_by}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Info */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-[#0F172A] mb-2">How Franchising Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-slate-600">
          <div>
            <h3 className="font-semibold text-slate-800 mb-1">What Each Franchise Gets</h3>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Full CRM functionality (clients, appraisals, quotes, invoices, expenses)</li>
              <li>Shared product catalog & suppliers (synced from your master data)</li>
              <li>Shared dropdown options, GST rates, estimate rates, roof conditions</li>
              <li>Their own admin account to manage their team</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 mb-1">What Franchises Cannot Do</h3>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Create further franchise copies</li>
              <li>Access Data Sync or Access database export</li>
              <li>See any of your client, job, or financial data</li>
              <li>Access the Franchise Manager</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
